import { FlashList } from '@shopify/flash-list';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, RefreshControl, ScrollView, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { RentalLocation, Reservation } from '@/api';
import { useDashboardMetrics } from '@/api/partners/dashboard/use-dashboard-metrics';
import {
  type CombinedHomeFeedReservation,
  useCombinedHomeFeedReservations,
} from '@/api/partners/reservations/use-combined-home-feed-reservations';
import { useToReviewReservations } from '@/api/partners/reservations/use-to-review-reservations';
import { useUpcomingReservations } from '@/api/partners/reservations/use-upcoming-reservations';
import { isDesktop, useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { notifyScrollVisibility } from '@/core/scroll-visibility';
import { Card, Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { Icon } from '@/ui/icon';
import { Home } from '@/ui/icons';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { Checkmark } from '@/ui/icons/checkmark';
import { Dot } from '@/ui/icons/dot';
import { Location } from '@/ui/icons/location';
import { Warning } from '@/ui/icons/warning';
import { Loader } from '@/ui/loader';
import { PillTabs } from '@/ui/pill-tabs';

import { NoCarsEmptyState } from '../first-car-empty-state';
import {
  CAR_ALERT_SHORT_LABEL,
  getPrimaryReservationCarAlert,
} from '../reservations/car-alerts';
import {
  getPartnerReservationStatusColor,
  getPartnerReservationStatusLabel,
} from '../reservations/status-label';
import { DashboardFilters } from './dashboard-filters';
import {
  type DashboardTab,
  parseDashboardMonthCount,
  parseDashboardTab,
} from './dashboard-route-params';
import { PerformanceView } from './performance-view';
import { RatingsView } from './ratings-view';

const DASHBOARD_TABS = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'performance', label: 'Performance' },
  { id: 'ratings', label: 'Ratings' },
] as const;

const ReservationItemSeparator = () => <View style={{ height: 16 }} />;

export default function Dashboard() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const params = useLocalSearchParams<{ tab?: string; months?: string }>();
  const status = useAuth.use.status();
  const isDesktopView = isDesktop();
  const [dashboardTab, setDashboardTab] = useState<DashboardTab>(
    () => parseDashboardTab(params.tab) ?? 'upcoming'
  );
  const [dashboardMonthCount, setDashboardMonthCount] = useState(() =>
    parseDashboardMonthCount(params.months)
  );
  const [dashboardCarId, setDashboardCarId] = useState<number | undefined>();

  const {
    data: dashboardMetricsData,
    isLoading: isLoadingDashboardMetrics,
    refetch: refetchDashboardMetrics,
  } = useDashboardMetrics({});

  const {
    data: toReviewReservationData,
    isFetchingNextPage: isFetchingNextPageToReview,
    fetchNextPage: fetchNextPageToReview,
    isLoading: isLoadingToReview,
    hasNextPage: hasNextPageToReview,
    refetch: refetchToReview,
    isSuccess: isToReviewSuccess,
  } = useToReviewReservations({}, { enabled: isDesktopView });

  const {
    data: upcomingReservationData,
    isFetchingNextPage: isFetchingNextPageUpcoming,
    fetchNextPage: fetchNextPageUpcoming,
    isLoading: isLoadingUpcoming,
    hasNextPage: hasNextPageUpcoming,
    refetch: refetchUpcoming,
    isSuccess: isUpcomingSuccess,
  } = useUpcomingReservations({}, { enabled: isDesktopView });

  const {
    data: combinedReservationData,
    isFetchingNextPage: isFetchingNextPageCombined,
    fetchNextPage: fetchNextPageCombined,
    isLoading: isLoadingCombined,
    hasNextPage: hasNextPageCombined,
    refetch: refetchCombined,
    isSuccess: isCombinedSuccess,
  } = useCombinedHomeFeedReservations({}, { enabled: !isDesktopView });

  useEffect(() => {
    if (status === 'signIn') {
      refetchDashboardMetrics();
      if (isDesktopView) {
        refetchToReview();
        refetchUpcoming();
      } else {
        refetchCombined();
      }
    }
  }, [
    isDesktopView,
    refetchCombined,
    refetchDashboardMetrics,
    refetchToReview,
    refetchUpcoming,
    status,
  ]);

  useEffect(() => {
    const tab = parseDashboardTab(params.tab);
    if (tab) {
      setDashboardTab(tab);
      return;
    }

    if (!params.tab) {
      setDashboardTab('upcoming');
    }
  }, [params.tab]);

  useEffect(() => {
    if (params.months !== undefined) {
      setDashboardMonthCount(parseDashboardMonthCount(params.months));
    }
  }, [params.months]);

  const handleDashboardTabChange = React.useCallback(
    (tab: DashboardTab) => {
      setDashboardTab(tab);

      if (tab === 'upcoming') {
        router.setParams({ tab: undefined, months: undefined });
        return;
      }

      router.setParams({
        tab,
        months: String(dashboardMonthCount),
      });
    },
    [dashboardMonthCount, router]
  );

  const handleDashboardMonthCountChange = React.useCallback(
    (monthCount: number) => {
      setDashboardMonthCount(monthCount);

      if (dashboardTab === 'performance' || dashboardTab === 'ratings') {
        router.setParams({
          tab: dashboardTab,
          months: String(monthCount),
        });
      }
    },
    [dashboardTab, router]
  );

  useFocusEffect(
    React.useCallback(() => {
      if (isDesktopView) {
        refetchToReview();
        refetchUpcoming();
      } else {
        refetchCombined();
      }
    }, [isDesktopView, refetchCombined, refetchToReview, refetchUpcoming])
  );

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Dashboard',
    });
  }, []);

  const toReviewReservations = useMemo(() => {
    return toReviewReservationData?.pages.map((page) => page.data).flat();
  }, [toReviewReservationData]);

  const upcomingReservations = useMemo(() => {
    return upcomingReservationData?.pages.map((page) => page.data).flat();
  }, [upcomingReservationData]);

  const combinedReservations = useMemo(() => {
    return combinedReservationData?.pages.map((page) => page.data).flat();
  }, [combinedReservationData]);

  const isLoadingReservations = isDesktopView
    ? isLoadingToReview || isLoadingUpcoming
    : isLoadingCombined;

  const isEmpty = isDesktopView
    ? isToReviewSuccess &&
      toReviewReservations?.length === 0 &&
      isUpcomingSuccess &&
      upcomingReservations?.length === 0
    : isCombinedSuccess && combinedReservations?.length === 0;
  const isFirstTimeEmptyState =
    (dashboardMetricsData?.totalCarsCount ?? 0) === 0 && isEmpty;

  type DashboardReservation = Reservation | CombinedHomeFeedReservation;
  const isPickupAction = (trip: DashboardReservation) => {
    return DateTime.fromISO(trip.startTime) > DateTime.now();
  };

  const getFeedTypeLabel = (trip: DashboardReservation) => {
    if ('feedType' in trip) {
      return trip.feedType === 'to_review' ? 'To Review' : 'Up Next';
    }
    return null;
  };

  const renderPickUpOrDelivery = (trip: DashboardReservation) => {
    let pickupOrDelivery: string;

    const isPickup = isPickupAction(trip);

    if (isPickup && trip.customPickupLocation) {
      pickupOrDelivery = 'Delivery';
    } else if (isPickup) {
      pickupOrDelivery = 'Pickup';
    } else if (trip.customReturnLocation) {
      pickupOrDelivery = 'Return';
    } else {
      pickupOrDelivery = 'Return';
    }

    return pickupOrDelivery;
  };

  const renderLocation = (trip: DashboardReservation) => {
    let location: RentalLocation | string;

    const isPickup = isPickupAction(trip);

    if (isPickup && trip.customPickupLocation) {
      location = trip.customPickupLocation;
    } else if (isPickup) {
      location = trip.pickupLocation.address;
    } else if (trip.customReturnLocation) {
      location = trip.customReturnLocation;
    } else {
      location = trip.returnLocation.address;
    }

    return location;
  };

  const getTimezone = (trip: DashboardReservation) => {
    const isPickup = isPickupAction(trip);

    const timezone = isPickup
      ? trip.pickupLocation.timezone
      : trip.returnLocation.timezone;

    return timezone;
  };

  const renderTime = (trip: DashboardReservation) => {
    const start = DateTime.fromISO(trip.startTime);
    const end = DateTime.fromISO(trip.endTime);

    const isPickup = isPickupAction(trip);

    const dateStr = isPickup
      ? start
          .setZone(trip.pickupLocation.timezone)
          .toFormat('MMM dd yyyy - h:mm a ZZZZ')
      : end
          .setZone(trip.returnLocation.timezone)
          .toFormat('MMM dd yyyy - h:mm a ZZZZ');

    return dateStr;
  };

  const renderLocalTime = (trip: DashboardReservation) => {
    const start = DateTime.fromISO(trip.startTime);
    const end = DateTime.fromISO(trip.endTime);

    const isPickup = isPickupAction(trip);

    const dateObj = isPickup
      ? start.setZone(DateTime.local().zoneName)
      : end.setZone(DateTime.local().zoneName);

    return `${dateObj.toFormat('h:mm a')} Local time (${dateObj.toFormat(
      'ZZZZ'
    )})`;
  };

  const renderItemCard = (item: DashboardReservation) => {
    const feedTypeLabel = getFeedTypeLabel(item);
    const primaryAlert = getPrimaryReservationCarAlert(item.status, item.car);

    return (
      <Pressable
        onPress={() => {
          router.push(`/partner/reservations/${item.id}`);
        }}
      >
        <Card>
          <View
            style={{
              padding: 16,
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.card.default.borderColor,
              flexDirection: isDesktopView ? 'row-reverse' : 'column',
              gap: isDesktopView ? 4 : 8,
            }}
          >
            {item.isPickedUp ? (
              <Chip
                type={ChipType.SUCCESS}
                icon={<Dot />}
                text="Guest picked up the car"
                iconWidth={8}
                iconHeight={8}
              />
            ) : null}

            <View style={isDesktopView ? { flex: 1 } : undefined}>
              <View>
                {feedTypeLabel !== null && (
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {feedTypeLabel}
                  </Text>
                )}

                <Text type="bodyBold">
                  {`${item.car.vehicleYear} ${item.car.brandName} ${item.car.modelName} (${item.car.licensePlate})`}
                </Text>
              </View>

              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Text
                  type="caption1"
                  style={{
                    color: getPartnerReservationStatusColor(
                      item.status,
                      item.isBlock
                    ),
                  }}
                >
                  {getPartnerReservationStatusLabel(item.status, item.isBlock)}
                </Text>

                <Text type="caption1">|</Text>

                {item.customPickupLocation ? (
                  <>
                    <Icon
                      type="naked"
                      icon={<Home />}
                      fillColor={theme.colors.icon.color}
                      width={14}
                      height={14}
                    />

                    <Text type="caption1">Delivery</Text>
                  </>
                ) : (
                  <Text type="caption1">Pickup & Return</Text>
                )}

                <Text type="caption1">|</Text>

                {item.subscription !== null ? (
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.chip.brand.color }}
                  >
                    Subscription
                  </Text>
                ) : (
                  <Text type="caption1">Daily Rental</Text>
                )}
              </View>
            </View>
          </View>

          <View style={{ padding: 16, gap: 8 }}>
            <View
              style={{
                flexDirection: 'row',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <Icon
                type="naked"
                icon={<CalendarIcon />}
                width={18}
                height={18}
                fillColor={theme.colors.icon.color}
              />

              <View style={{ gap: 4 }}>
                <Text type="caption1" style={{ flexShrink: 1 }}>
                  {`${renderPickUpOrDelivery(item)} time: ${renderTime(item)}`}
                </Text>

                {DateTime.local().zoneName !== getTimezone(item) && (
                  <Text
                    type="caption2"
                    style={{
                      flexShrink: 1,
                      color: theme.colors.text.secondary,
                    }}
                  >
                    {renderLocalTime(item)}
                  </Text>
                )}
              </View>
            </View>

            <View
              style={{
                flexDirection: 'row',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <Icon
                type="naked"
                icon={<Location />}
                width={18}
                height={18}
                fillColor={theme.colors.icon.color}
              />

              <Text type="caption1" style={{ flexShrink: 1 }}>
                {`${renderLocation(item)}`}
              </Text>
            </View>

            {primaryAlert ? (
              <View
                style={{
                  flexDirection: 'row',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <Icon
                  type="naked"
                  icon={<Warning />}
                  width={18}
                  height={18}
                  fillColor={theme.colors.warning}
                />

                <Text
                  type="caption1"
                  style={{ flexShrink: 1, color: theme.colors.warning }}
                >
                  {CAR_ALERT_SHORT_LABEL[primaryAlert]}
                </Text>
              </View>
            ) : null}
          </View>
        </Card>
      </Pressable>
    );
  };

  const renderCombinedItem = ({
    item,
  }: {
    item: CombinedHomeFeedReservation;
  }) => renderItemCard(item);

  const renderUpcomingItem = ({ item }: { item: Reservation }) =>
    renderItemCard(item);

  const renderToReviewItem = ({ item }: { item: Reservation }) =>
    renderItemCard(item);

  if (isLoadingReservations) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  if (isFirstTimeEmptyState) {
    return (
      <NoCarsEmptyState
        title="Add Your First Car"
        body="Add your first car to start managing your fleet"
        ctaText="Add Your First Car"
        redirectPath="partner/cars/setup?fromSf=1"
      />
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
      }}
    >
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
        onScroll={() => notifyScrollVisibility()}
        scrollEventThrottle={100}
        refreshControl={
          <RefreshControl
            refreshing={isLoadingReservations || isLoadingDashboardMetrics}
            onRefresh={async () => {
              await refetchDashboardMetrics();
              if (isDesktopView) {
                await refetchToReview();
                await refetchUpcoming();
              } else {
                await refetchCombined();
              }
            }}
          />
        }
      >
        <View style={styles.header}>
          <View style={{ flex: 1 }}>
            <Text type="title1">Dashboard</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              A complete view of your fleet, earnings, and customer experience.
            </Text>
          </View>
        </View>

        <View style={styles.body}>
          <View style={styles.tabBar}>
            <PillTabs
              tabs={DASHBOARD_TABS}
              activeId={dashboardTab}
              onChange={handleDashboardTabChange}
            />
            {dashboardTab === 'performance' || dashboardTab === 'ratings' ? (
              <DashboardFilters
                monthCount={dashboardMonthCount}
                onMonthCountChange={handleDashboardMonthCountChange}
                carId={dashboardCarId}
                onCarIdChange={setDashboardCarId}
                showCarFilter={dashboardTab === 'performance'}
              />
            ) : null}
          </View>

          {dashboardTab === 'performance' ? (
            <PerformanceView
              metrics={dashboardMetricsData}
              monthCount={dashboardMonthCount}
              carId={dashboardCarId}
            />
          ) : dashboardTab === 'ratings' ? (
            <RatingsView monthCount={dashboardMonthCount} />
          ) : (
            <>
              <View style={styles.upNextToReviewContainer}>
                {isEmpty ? (
                  <View
                    style={{
                      flex: 1,
                      gap: 16,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon
                      type="dressed"
                      icon={<Checkmark />}
                      fillColor={theme.colors.icon.color}
                      width={16}
                      height={16}
                      style={{
                        borderRadius: 28,
                      }}
                    />

                    <View style={{ alignItems: 'center', gap: 8 }}>
                      <Text type="title2">All Caught Up</Text>

                      <Text
                        type="bodyRegular"
                        style={{
                          color: theme.colors.text.secondary,
                          textAlign: 'center',
                        }}
                      >
                        Your upcoming pick-ups, drop-offs, and reviews will
                        appear here once bookings come in.
                      </Text>
                    </View>
                  </View>
                ) : !isDesktopView ? (
                  <View style={{ flex: 1, gap: 24 }}>
                    <View>
                      <Text type="title2">Up Next & To Review</Text>

                      <Text
                        type="bodyRegular"
                        style={{
                          color: theme.colors.text.secondary,
                          gap: 4,
                        }}
                      >
                        Your upcoming pick-ups, drop-offs, and items to review
                      </Text>
                    </View>

                    <FlashList
                      data={combinedReservations}
                      renderItem={renderCombinedItem}
                      ItemSeparatorComponent={ReservationItemSeparator}
                      onEndReached={() => {
                        if (
                          hasNextPageCombined &&
                          !isFetchingNextPageCombined
                        ) {
                          fetchNextPageCombined();
                        }
                      }}
                      estimatedItemSize={20}
                      showsVerticalScrollIndicator
                      contentContainerStyle={{}}
                      ListFooterComponent={
                        isFetchingNextPageCombined ? (
                          <Loader style={{ height: 140 }} />
                        ) : null
                      }
                    />
                  </View>
                ) : (
                  <>
                    <View style={{ flex: 1, gap: 24 }}>
                      <View>
                        <Text type="title2">Up Next</Text>

                        <Text
                          type="bodyRegular"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          Manage all the upcoming pick-ups and drop-offs
                        </Text>
                      </View>

                      <FlashList
                        data={upcomingReservations}
                        renderItem={renderUpcomingItem}
                        ItemSeparatorComponent={ReservationItemSeparator}
                        onEndReached={() => {
                          if (
                            hasNextPageUpcoming &&
                            !isFetchingNextPageUpcoming
                          ) {
                            console.log('end reached');
                            fetchNextPageUpcoming();
                          }
                        }}
                        estimatedItemSize={20}
                        showsVerticalScrollIndicator
                        contentContainerStyle={{}}
                        ListFooterComponent={
                          isFetchingNextPageUpcoming ? (
                            <Loader style={{ height: 140 }} />
                          ) : null
                        }
                      />
                    </View>

                    <View style={{ flex: 1, gap: 24 }}>
                      <View>
                        <Text type="title2">To Review</Text>

                        <Text
                          type="bodyRegular"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          Review reimbursements, supercharging and tolls
                        </Text>
                      </View>

                      <FlashList
                        data={toReviewReservations}
                        renderItem={renderToReviewItem}
                        ItemSeparatorComponent={ReservationItemSeparator}
                        onEndReached={() => {
                          if (
                            hasNextPageToReview &&
                            !isFetchingNextPageToReview
                          ) {
                            console.log('end reached');
                            fetchNextPageToReview();
                          }
                        }}
                        estimatedItemSize={20}
                        showsVerticalScrollIndicator
                        contentContainerStyle={{}}
                        ListFooterComponent={
                          isFetchingNextPageToReview ? (
                            <Loader style={{ height: 140 }} />
                          ) : null
                        }
                      />
                    </View>
                  </>
                )}
              </View>
            </>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: {
      xs: 20,
      md: 56,
    },
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingBottom: {
      xs: 24,
      md: 56,
    },
    gap: {
      xs: 16,
      md: 64,
    },
  },
  header: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  body: {
    flexGrow: 1,
    gap: {
      xs: 24,
      md: 32,
    },
  },
  tabBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    zIndex: 3,
  },
  upNextToReviewContainer: {
    flex: 1,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    gap: 24,
  },
}));
